/**
 * Certificates — ADMIN only.
 *
 * Recognition, which the client document's programmes end with and which had
 * nothing behind it until now: "appreciation email and digital certificate,
 * within a month of handover".
 *
 * ---------------------------------------------------------------------------
 * What this screen is careful about
 * ---------------------------------------------------------------------------
 * Issuing is not undoable and it emails somebody. So:
 *
 *   - The figures are shown BEFORE the button is offered. An admin sees the
 *     hours, the number of activities and the period, and can expand a row to
 *     read the itemised list — the same list that will be printed. Nobody has
 *     to trust a button.
 *   - A row already issued says so, with its reference and date, and says when
 *     there are new hours since. The button then reads "Issue again" rather
 *     than pretending nothing has happened.
 *   - Bulk issue asks first, and names the number. Selecting rows is easy to do
 *     by accident; sending twenty emails should not be.
 *
 * Rows are not clickable. `DataTable` makes a row a button when `onRowClick` is
 * given, and a row that both navigates and holds two buttons is a row where
 * every click is a guess — `AttendanceTable` has the same buttons-not-navigation
 * shape for the same reason.
 */

import { useCallback, useMemo, useState } from 'react';
import { MAX_CERTIFICATES_PER_REQUEST, formatDuration } from '@parinaam-vms/shared';
import type { VolunteerCertificateSummary } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Pager,
  PageHeader,
  SelectInput,
  StatusBadge,
  Tabs,
  TextInput,
} from '../../components/common';
import type { Column } from '../../components/common';
import {
  useCertificateSummaries,
  useDownloadCertificate,
  useIssueCertificates,
  useVolunteerActivities,
} from '../../hooks/useCertificates';
import { useAuth } from '../../hooks/useAuth';
import { fetchStandardPreviewPdf } from '../../services/api/certificates';
import { formatDate } from '../../utils/format';
import { CustomCertificateTab } from './CustomCertificateTab';
import { VolunteerCertificatesTab } from './VolunteerCertificatesTab';
import { CertificateViewer } from './CertificateViewer';
import styles from './CertificatesPage.module.css';

const ISSUED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'NEVER', label: 'Not yet issued' },
  { value: 'ISSUED', label: 'Already issued' },
];
export function CertificatesPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ADMIN');
  const isTeam = hasRole('TEAM');
  const [tab, setTab] = useState('standard');

  if (!isAdmin && !isTeam) {
    return (
      <>
        <PageHeader title="Certificates" />
        <Card>
          <EmptyState
            title="Staff only"
            message="Certificates name a volunteer and total their hours, so they need a staff account."
          />
        </Card>
      </>
    );
  }

  /*
   * A coordinator gets the reading tab and nothing else.
   *
   * Issuing writes to a volunteer on Parinaam's behalf and stays with an admin
   * — the API enforces that, and this only matches it, so a coordinator is
   * never shown a button that would answer 403. Looking up what has already
   * been issued is a different act, and it is one a coordinator is asked to do.
   */
  const tabs = [
    ...(isAdmin
      ? [
          { id: 'standard', label: 'Volunteer Hours', content: <ApprovedHoursTab /> },
          { id: 'custom', label: 'Custom Certificate', content: <CustomCertificateTab /> },
        ]
      : []),
    { id: 'volunteer', label: 'By volunteer', content: <VolunteerCertificatesTab /> },
  ];

  /*
   * The role arrives with the session, which may land after the first render,
   * so the default tab can be one this account cannot see. Falling back to the
   * first available tab is what keeps a coordinator from meeting a blank panel
   * for the instant before the roles settle.
   */
  const active = tabs.some((candidate) => candidate.id === tab) ? tab : (tabs[0]?.id ?? 'volunteer');

  return (
    <>
      <PageHeader title="Certificates" />
      <Tabs label="Certificates" active={active} onChange={setTab} tabs={tabs} />
    </>
  );
}

/**
 * Certificates generated from approved attendance — the feature as it existed
 * before the custom-certificate tab (migration 044). Split out of
 * `CertificatesPage` so each tab mounts, and queries, only while it is showing.
 */
function ApprovedHoursTab() {
  const [search, setSearch] = useState('');
  const [issued, setIssued] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmingBulk, setConfirmingBulk] = useState(false);

  const query = useCertificateSummaries({
    search: search.trim() || undefined,
    issued: (issued || undefined) as 'ISSUED' | 'NEVER' | undefined,
    page,
  });

  const issue = useIssueCertificates();
  const download = useDownloadCertificate();

  /** The row whose draft is on screen, if any. */
  const [preview, setPreview] = useState<{ volunteer_id: string; name: string } | null>(null);

  /*
   * Memoised on the id, because `CertificateViewer` refetches whenever this
   * changes identity — an inline arrow would rebuild the PDF on every keystroke
   * in the search box.
   */
  const previewId = preview?.volunteer_id;
  const loadPreview = useCallback(() => {
    if (!previewId) return Promise.reject(new Error('No volunteer selected.'));
    return fetchStandardPreviewPdf(previewId);
  }, [previewId]);

  const rows = query.data?.items ?? [];

  /**
   * Which rows the last issue declined, and why, keyed by volunteer.
   *
   * Held on the screen rather than in a toast: a toast that named one of
   * nineteen skipped volunteers would be useless, and one that named all
   * nineteen would be unreadable. The reason belongs on the row it is about.
   */
  const skipped = useMemo(() => {
    const reasons = new Map<string, string>();
    for (const outcome of issue.data?.outcomes ?? []) {
      if (!outcome.issued && outcome.reason) reasons.set(outcome.volunteer_id, outcome.reason);
    }
    return reasons;
  }, [issue.data]);

  /** Only rows on this page can be selected, so this cannot select what is not shown. */
  const selectedOnPage = rows.filter((row) => selected.has(row.volunteer_id));
  const allOnPageSelected = rows.length > 0 && selectedOnPage.length === rows.length;

  /**
   * Selection survives paging, so it can exceed what one request may carry.
   *
   * The cap is shared rather than repeated (`MAX_CERTIFICATES_PER_REQUEST`), so
   * the button disables at exactly the number the API refuses at instead of the
   * admin discovering it from a 422 that names no row.
   */
  const overCap = selected.size > MAX_CERTIFICATES_PER_REQUEST;

  function toggle(volunteerId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(volunteerId)) next.delete(volunteerId);
      else next.add(volunteerId);
      return next;
    });
  }

  function toggleAllOnPage() {
    setSelected((current) => {
      const next = new Set(current);
      if (allOnPageSelected) rows.forEach((row) => next.delete(row.volunteer_id));
      else rows.forEach((row) => next.add(row.volunteer_id));
      return next;
    });
  }

  function issueTo(volunteerIds: string[]) {
    issue.mutate(volunteerIds, {
      onSuccess: () => {
        setConfirmingBulk(false);
        // Clear only what was just acted on. Anything the admin selected on
        // another page is still selected, and still unissued.
        setSelected((current) => {
          const next = new Set(current);
          volunteerIds.forEach((id) => next.delete(id));
          return next;
        });
      },
    });
  }

  const columns: Column<VolunteerCertificateSummary>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allOnPageSelected}
          onChange={toggleAllOnPage}
          aria-label="Select every volunteer on this page"
          disabled={rows.length === 0}
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selected.has(row.volunteer_id)}
          onChange={() => toggle(row.volunteer_id)}
          aria-label={`Select ${row.volunteer_name}`}
        />
      ),
    },
    { key: 'code', header: 'ID', render: (row) => row.volunteer_code },
    {
      key: 'name',
      header: 'Volunteer',
      render: (row) => (
        <div className={styles.nameCell}>
          <span className={styles.name}>{row.volunteer_name}</span>
          {row.organization_name && <span className={styles.subtle}>{row.organization_name}</span>}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Email',
      render: (row) =>
        row.email ?? (
          // Said plainly, because it changes what issuing does: the certificate
          // is created and audited, and there is nowhere to send it.
          <span
            className={styles.noEmail}
            title="No email on file — the certificate can only be downloaded"
          >
            none on file
          </span>
        ),
    },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'hours',
      header: 'Approved hours',
      numeric: true,
      render: (row) => formatDuration(row.total_minutes),
    },
    {
      key: 'activities',
      header: 'Activities',
      center: true,
      render: (row) => (
        <button
          type="button"
          className={styles.expand}
          onClick={() => setExpanded(expanded === row.volunteer_id ? null : row.volunteer_id)}
          aria-expanded={expanded === row.volunteer_id}
        >
          {row.activity_count}
        </button>
      ),
    },
    {
      key: 'period',
      header: 'Period',
      render: (row) => (
        <span className={styles.subtle}>
          {formatDate(row.first_attendance_date)} – {formatDate(row.last_attendance_date)}
        </span>
      ),
    },
    {
      key: 'issued',
      header: 'Last issued',
      render: (row) => <LastIssued row={row} />,
    },
    {
      key: 'action',
      header: '',
      render: (row) => {
        const reason = skipped.get(row.volunteer_id);
        return (
          <div className={styles.actions}>
            {/*
              Preview sits before Issue, in the order the decision is made:
              read the document, then commit to it. It is a draft of what would
              be sent now, not a copy of anything already issued — the "Last
              issued" column is where an existing one is opened.
            */}
            <Button
              variant="secondary"
              onClick={() =>
                setPreview(
                  preview?.volunteer_id === row.volunteer_id
                    ? null
                    : { volunteer_id: row.volunteer_id, name: row.volunteer_name }
                )
              }
            >
              {preview?.volunteer_id === row.volunteer_id ? 'Hide' : 'Preview'}
            </Button>
            <Button
              variant={row.last_certificate_code ? undefined : 'primary'}
              loading={issue.isPending && issue.variables?.includes(row.volunteer_id)}
              disabled={issue.isPending}
              onClick={() => issueTo([row.volunteer_id])}
            >
              {row.last_certificate_code ? 'Issue again' : 'Issue'}
            </Button>
            {reason && <span className={styles.skipReason}>{reason}</span>}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Card>
        <p className={styles.intro}>
          Certificates recognise <strong>approved</strong> hours only, and count a group session as
          everyone who attended it. Anyone whose hours are still awaiting approval will not appear
          here with them.
        </p>

        <div className={styles.filters}>
          <TextInput
            label="Search"
            value={search}
            placeholder="Name, ID or email"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <SelectInput
            label="Show"
            value={issued}
            options={ISSUED_OPTIONS}
            onChange={(event) => {
              setIssued(event.target.value);
              setPage(1);
            }}
          />
        </div>

        {selected.size > 0 && (
          <div className={styles.bulk} role="region" aria-label="Selected volunteers">
            {confirmingBulk ? (
              <div className={styles.confirm} role="alert">
                <p>
                  Issue a certificate to <strong>{selected.size}</strong>{' '}
                  {selected.size === 1 ? 'volunteer' : 'volunteers'}? Each one with an email address
                  on file will be sent their certificate. This cannot be undone.
                </p>
                <div className={styles.confirmActions}>
                  <Button
                    variant="primary"
                    loading={issue.isPending}
                    onClick={() => issueTo([...selected])}
                  >
                    Issue {selected.size}
                  </Button>
                  <Button onClick={() => setConfirmingBulk(false)} disabled={issue.isPending}>
                    Go back
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.bulkBar}>
                <span>
                  {selected.size} selected
                  {selectedOnPage.length !== selected.size && (
                    <span className={styles.subtle}> (some on other pages)</span>
                  )}
                  {overCap && (
                    /*
                     * Said here, before the button is pressed. The API refuses
                     * the whole request over the cap, so without this an admin
                     * who selected across three pages would get one 422 naming
                     * no row, keep their selection, and have to guess the
                     * number.
                     */
                    <span className={styles.overCap}>
                      {' '}
                      — at most {MAX_CERTIFICATES_PER_REQUEST} at a time. Deselect{' '}
                      {selected.size - MAX_CERTIFICATES_PER_REQUEST} to continue.
                    </span>
                  )}
                </span>
                <div className={styles.confirmActions}>
                  <Button
                    variant="primary"
                    onClick={() => setConfirmingBulk(true)}
                    disabled={overCap}
                  >
                    Issue to selected
                  </Button>
                  <Button onClick={() => setSelected(new Set())}>Clear</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {query.isPending ? (
          <LoadingState label="Working out who has hours to recognise…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nobody to certify yet"
            message={
              search || issued
                ? 'No volunteer matches these filters. Clear them to see everyone with approved hours.'
                : 'A volunteer appears here once some of their attendance has been approved. Approve hours on the Attendance screen and they will show up.'
            }
          />
        ) : (
          <>
            <DataTable<VolunteerCertificateSummary>
              caption="Volunteers with approved hours"
              rows={rows}
              columns={columns}
              getRowKey={(row) => row.volunteer_id}
              renderExpanded={(row) =>
                expanded === row.volunteer_id ? (
                  <ActivityBreakdown volunteerId={row.volunteer_id} />
                ) : null
              }
            />
            <Pager pagination={query.data?.pagination} onPageChange={setPage} />
          </>
        )}
      </Card>

      {preview && (
        <CertificateViewer
          title="Draft — not yet issued"
          subtitle={`What ${preview.name}'s certificate would say if it were issued today. Nothing has been sent.`}
          source={loadPreview}
          downloadName="Parinaam-Certificate-DRAFT.pdf"
          onClose={() => setPreview(null)}
        />
      )}

      {issue.data && issue.data.issued > 0 && (
        <Card title="Issued">
          <ul className={styles.issuedList}>
            {issue.data.outcomes
              .filter((outcome) => outcome.certificate)
              .map((outcome) => (
                <li key={outcome.certificate?.certificate_id}>
                  <span className={styles.issuedCode}>{outcome.certificate?.certificate_code}</span>{' '}
                  {outcome.certificate?.volunteer_name}{' '}
                  <span className={styles.subtle}>
                    · {formatDuration(outcome.certificate?.total_minutes ?? 0)}
                  </span>{' '}
                  <Button
                    onClick={() =>
                      download.mutate({
                        certificate_id: outcome.certificate?.certificate_id ?? '',
                        certificate_code: outcome.certificate?.certificate_code ?? '',
                      })
                    }
                    loading={
                      download.isPending &&
                      download.variables?.certificate_id === outcome.certificate?.certificate_id
                    }
                  >
                    Download PDF
                  </Button>
                  {!outcome.certificate?.recipient_email && (
                    <span className={styles.skipReason}>
                      no email on file — download and hand it over
                    </span>
                  )}
                </li>
              ))}
          </ul>
        </Card>
      )}
    </>
  );
}

/**
 * What the last certificate said, and whether it is still current.
 *
 * The comparison is the useful part: "22h since" tells an admin there is
 * something new to recognise without opening anything or doing arithmetic.
 *
 * The reference is a **download**, not a label. Migration 034's promise is that
 * fetching an old certificate returns the document that was sent, and this is
 * where an admin asked for a copy acts on it — without it the only way to
 * answer "could you send Asha's certificate again" would be to issue a second
 * one, which would email her twice and leave two records of one recognition.
 */
function LastIssued({ row }: { row: VolunteerCertificateSummary }) {
  const download = useDownloadCertificate();

  if (!row.last_certificate_code || !row.last_issued_at) {
    return <span className={styles.subtle}>—</span>;
  }

  const since = row.total_minutes - (row.last_certificate_minutes ?? 0);

  return (
    <div className={styles.nameCell}>
      {row.last_certificate_id ? (
        <button
          type="button"
          className={styles.expand}
          disabled={download.isPending}
          onClick={() =>
            download.mutate({
              certificate_id: row.last_certificate_id as string,
              certificate_code: row.last_certificate_code as string,
            })
          }
          title="Download this certificate again"
        >
          {row.last_certificate_code}
        </button>
      ) : (
        <span>{row.last_certificate_code}</span>
      )}
      <span className={styles.subtle}>{formatDate(row.last_issued_at)}</span>
      {since > 0 && <span className={styles.newHours}>+{formatDuration(since)} since</span>}
    </div>
  );
}

/**
 * The itemised activities, loaded only when a row is opened.
 *
 * A child component so the query is mounted with the panel: twenty rows firing
 * twenty breakdown queries on render would be a load test, not a table.
 */
function ActivityBreakdown({ volunteerId }: { volunteerId: string }) {
  const query = useVolunteerActivities(volunteerId);

  if (query.isPending) return <LoadingState label="Loading activities…" />;
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const activities = query.data ?? [];
  if (activities.length === 0) {
    return <p className={styles.subtle}>No approved activities.</p>;
  }

  return (
    <table className={styles.breakdown}>
      <caption className="visually-hidden">Activities counted towards this certificate</caption>
      <thead>
        <tr>
          <th scope="col">Programme</th>
          <th scope="col">Activity</th>
          <th scope="col">Days</th>
          <th scope="col">Hours</th>
          <th scope="col">Period</th>
        </tr>
      </thead>
      <tbody>
        {activities.map((activity) => (
          <tr key={activity.activity_id}>
            <td>{activity.program_name}</td>
            <td>{activity.activity_name}</td>
            <td>{activity.days}</td>
            <td>{formatDuration(activity.minutes)}</td>
            <td className={styles.subtle}>
              {activity.first_date === activity.last_date
                ? formatDate(activity.first_date)
                : `${formatDate(activity.first_date)} – ${formatDate(activity.last_date)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
